import { useEffect, useRef, useState } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { Box } from '@gemini/core';
import { CustomEventService, IEvents } from '@mfe-helpers/customEvent';
import { composePageViewPayload } from '@mfe-helpers/tracking';
import Animation from '@mfe-presentation/components/Animation';
import Icon from '@mfe-presentation/components/Icon';
import LoadingMessage from '@mfe-presentation/components/LoadingMessage';
import {
    firstTextTimeOut,
    isDoneTimeOut,
    isProgressingTimeOut,
    secondTextTimeOut,
    thirdTextTimeOut,
} from '@mfe-presentation/constants/loading';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import { EActionType } from '@mfe-types/context';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { useNavigate } from 'react-router-dom';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';
import {
    progressHeightDesktop,
    progressHeightTablet,
    topbarHeight,
} from '@mfe-presentation/constants/layout';

const Loading = () => {
    const [isDone, setIsDone] = useState(false);
    const { t } = useTranslation(NAME_SPACE);
    const { estimations, config, stepNames, portal, formValues, useShellContext } = useAppContext();
    const shellContext = useShellContext();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const text = {
        firstText: t('step_loading.list.gathering'),
        secondText: t('step_loading.list.finding'),
        thirdText: t('step_loading.list.calculating'),
        ready: t('step_loading.list.ready'),
        headlineInProgress: t('step_loading.header.title.inProgress'),
        headlineIsDone: t('step_loading.header.title.done'),
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (estimations.id) {
                setIsDone(true);
            }
        }, isProgressingTimeOut);
        return () => clearTimeout(timer);
    }, [estimations.valuation]);

    const searchParams = useRef(window.location.search);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (isDone) {
                dispatch({
                    type: EActionType.SetCurrentStep,
                    payload: { name: 'address', index: 0 },
                });
                navigate('../result' + searchParams.current, { replace: true });
            }
        }, isDoneTimeOut);

        return () => clearTimeout(timer);
    }, [isDone]);

    useEffect(() => {
        const name = 'loading';
        const index = stepNames.length - 1;
        dispatch({
            type: EActionType.SetCurrentStep,
            payload: {
                name,
                index,
                displayedStepNumber: config.loading.displayedStepNumber,
            },
        });

        const eventPayload = composePageViewPayload({
            stepIndex: index,
            pageTitle: `estimate_step${index}_${name}`,
            portal: portal as Portal,
            formValues,
        });

        CustomEventService.fire(IEvents.SEND_PAGE_VIEW, eventPayload);
    }, []);

    return (
        <Box marginHorizontal="auto" marginTop="spacing.40">
            {!isDone ? ( // TODO: refactor and keep code DRY
                <>
                    <Box display="flex" justifyContent="center" margin="auto">
                        {isFrenchPortal(portal) ? (
                            <Icon width="130" height="130" name="calculation-in-progress" />
                        ) : (
                            <Animation
                                key="calculation-progress"
                                name="calculation-progress"
                                data={shellContext?.animations?.calculationProgress}
                            />
                        )}
                    </Box>
                    <Box
                        as="header"
                        margin="auto"
                        textAlign={{
                            'breakpoint.base': 'left',
                            'breakpoint.xs': 'center',
                        }}
                        marginBottom={{
                            'breakpoint.xs': 'spacing.16',
                        }}
                    >
                        <Box
                            as="h1"
                            typography={{
                                'breakpoint.base': 'typography.headline.24.bold',
                                'breakpoint.xs': 'typography.headline.28.bold',
                            }}
                        >
                            {text.headlineInProgress}
                        </Box>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        width="320px" // fixed width used for visual stability when all LoadingMessage components have not same length (DE case)
                        margin="auto"
                    >
                        <LoadingMessage text={text.firstText} displayedAfter={firstTextTimeOut} />
                        <LoadingMessage text={text.secondText} displayedAfter={secondTextTimeOut} />
                        <LoadingMessage text={text.thirdText} displayedAfter={thirdTextTimeOut} />
                    </Box>
                </>
            ) : (
                <>
                    {!isFrenchPortal(portal) && (
                        <Box
                            position="fixed"
                            top={{
                                'breakpoint.base': `${topbarHeight + progressHeightTablet}px`,
                                'breakpoint.sm': `${topbarHeight + progressHeightDesktop}px`,
                            }}
                            left="0"
                            width="100%"
                            height="100%"
                            zIndex="zIndex.4"
                        >
                            <Animation
                                key="confettis"
                                name="confettis"
                                data={shellContext?.animations?.confettis}
                            />
                        </Box>
                    )}
                    <Box display="flex" justifyContent="center" margin="auto">
                        {isFrenchPortal(portal) ? (
                            <Icon width="130" height="130" name="calculation-done" />
                        ) : (
                            <Animation
                                key="calculation-complete"
                                name="calculation-complete"
                                data={shellContext?.animations?.calculationComplete}
                            />
                        )}
                    </Box>
                    <Box
                        as="header"
                        margin="auto"
                        textAlign={{
                            'breakpoint.base': 'left',
                            'breakpoint.xs': 'center',
                        }}
                        marginBottom={{
                            'breakpoint.xs': 'spacing.16',
                        }}
                    >
                        <Box
                            as="h1"
                            typography={{
                                'breakpoint.base': 'typography.headline.24.bold',
                                'breakpoint.xs': 'typography.headline.28.bold',
                            }}
                        >
                            {text.headlineIsDone}
                        </Box>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        width="320px"
                        margin="auto"
                    >
                        <Box
                            as="p"
                            typography="typography.body.14.regular"
                            marginTop="spacing.16"
                            marginBottom="spacing.12"
                            color="color.content.default.default"
                        >
                            {text.ready}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default Loading;
